
import React, { useState, FormEvent, useEffect, ChangeEvent } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from 'hooks/useQuery';
import {
  addDays,
  addHours,
  differenceInCalendarDays,
  differenceInDays,
  format
} from 'date-fns';

import HeaderMagraGroup from 'img/header-mafra-group.svg';
import HeaderLogoCCare from 'img/customer-care-header-logo.svg';
import LogoCompanies from 'img/companies-viveo-horizontal.png';

import { SchedulingSuccess } from 'components/DeliveryScheduling/SchedulingSuccess';
import { SchedulingWarning } from 'components/DeliveryScheduling/SchedulingWarning';
import { Column, Row } from 'components/Layout/Grid';
import { Visibility } from 'components/Visibility';
import { Button } from 'components/Button';
import TextArea from 'components/TextArea';
import Input from 'components/Input';
import Text from 'components/Text';

import {
  addDaysNewDate,
  checkIfIsHoliday,
  checkIfIsWeekend,
  concatDateAndTimeFormatToISODate
} from 'utils/Date';
import { DateOutputStr } from 'utils/DateOutput';

import TicketServices from 'services/ticket.service';
import HolidayService from 'services/holiday.service';

import { ScheduleTicketProps } from 'models/Ticket';
import {
  arrayTimes,
  BusinessDaysArray,
  EnumErrorCodeRequest,
  ErrorCatchAxios,
  ErrorState,
  FormFieldsDataProps,
  HolidayProps,
} from './interface';

import {
  Container,
  Content,
  Footer,
  FormSchedule,
  TableGrid,
  TableGridDate,
  ButtonGridItem,
  TableRow
} from './styles';

export const DeliveryScheduling = () => {

  const [errorRequest, setErrorRequest] = useState<ErrorState>({
    isError: false,
    field: "",
    message: "",
  });
  const [errorField, setErrorField] = useState<ErrorState>({
    isError: false,
    message: "",
  });

  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [dataInput, setDataInput] = useState<FormFieldsDataProps>({
    date: "",
    time: "",
    description: ""
  });
  const [dateTimeGrid, setDateTimeGrid] = useState<string>("");
  const [dateHoliday, setDateHoliday] = useState<HolidayProps[]>([]);
  const [forecastDate, setForecastDate] = useState<string>("");

  const minDate: string = DateOutputStr(getMinDate().toISOString(), 'yyyy-MM-dd');

  const protocolIdParams: string | null = useQuery().get('id') ?? "";
  const nfParams: string | null = useQuery().get('ref') ?? "";
  const forecastDateParams: string = useQuery().get('exp') ?? "";

  const _ticketService = new TicketServices();
  const _holidayService = new HolidayService();

  useEffect(() => {
    let dateExpFinal = "";

    if (!forecastDateParams || forecastDateParams === "0") {
      const formatNewDate = format(new Date(), 'yyyy/MM/dd');
      dateExpFinal = addDaysNewDate(formatNewDate, 1).toString();
    } else {
      const year = forecastDateParams.toString()?.slice(0, 4);
      const month = forecastDateParams.toString()?.slice(4, 6);
      const day = forecastDateParams.toString()?.slice(6, 8);

      dateExpFinal = year + "/" + month + "/" + day;
    }

    setForecastDate(dateExpFinal);

  }, [forecastDateParams]);


  useEffect(() => {
    _holidayService.getHoliday(protocolIdParams)
      .then((response) => {
        setDateHoliday(response.data);
      })
      .catch((error: AxiosError<ErrorCatchAxios>) => {
        setErrorRequest({
          isError: true,
          field: EnumErrorCodeRequest.GENERIC_ERROR,
          message: error.response?.data.data,
        });
      });
  }, []);

  useEffect(() => {
    if (errorField.isError && (dateTimeGrid || (dataInput.date && dataInput.time))) {
      setErrorField({
        isError: false,
        message: ""
      });
    }
  }, [dateTimeGrid, dataInput]);

  function getMinDate() {
    const minDate = new Date(new Date().toDateString());
    minDate.setDate(minDate.getDate() + 1);
    return minDate;
  }

  const getValidateAndReturnBusinessDays = () => {
    if (forecastDate) {

      const forecastNextDay: Date = addDaysNewDate(forecastDate, 1);
      const daysAvailable: number = returnNumberOfDaysAvailable(forecastNextDay);

      const businessDaysArray = validateAndReturnBusinessDaysWithDaysDifference(forecastNextDay, daysAvailable);
      const nameDays = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
      const objDays: BusinessDaysArray[] = [];

      businessDaysArray.forEach((date: Date) => {
        objDays.push({
          nameDays: nameDays[date.getDay()],
          dateFull: format(date, 'yyyy/MM/dd'),
          date: format(date, 'dd/MM'),
        });
      });

      return objDays;
    }

    return [];
  };

  const returnNumberOfDaysAvailable = (initialDate: Date) => {
    const businessDays = returnsBusinessDays(initialDate);

    let totalDays = 0;

    businessDays.forEach(day => {
      const differenceInDays = differenceInCalendarDays(day, new Date());

      if (differenceInDays > 0)
        totalDays = totalDays + 1;
    });

    return totalDays;
  };

  const returnsBusinessDays = (initialDate: Date) => {
    const arrayDays = [];

    let indexDate = initialDate;

    while (arrayDays.length < 7) {
      const isWeekend = checkIfIsWeekend(indexDate);
      const isHoliday = checkIfIsHoliday(indexDate, dateHoliday);

      if (!isWeekend && !isHoliday) {
        const formattedDateString = format(indexDate, 'yyyy/MM/dd');
        arrayDays.push(new Date(formattedDateString));
      }

      indexDate = addDays(indexDate, 1);
    }

    return arrayDays;
  };

  const validateAndReturnBusinessDaysWithDaysDifference = (initialDate: Date, daysAvailable: number) => {

    const nextDay: Date = addDays(new Date(), 1);
    const arrayDays = [];

    let indexDate = initialDate;

    while (arrayDays.length < daysAvailable) {
      const isWeekend = checkIfIsWeekend(indexDate);
      const isHoliday = checkIfIsHoliday(indexDate, dateHoliday);
      const differenceDay = differenceInDays(nextDay, indexDate);

      if (!isWeekend && !isHoliday && differenceDay < 1) {
        const formattedDateString = format(indexDate, 'yyyy/MM/dd');
        arrayDays.push(new Date(formattedDateString));
      }

      indexDate = addDays(indexDate, 1);
    }

    return arrayDays;
  };

  const handleChange = (event: ChangeEvent<any>) => {
    const { value } = event.target;
    const { statename } = event.target.dataset;

    setDataInput((prevState: any) => ({
      ...prevState,
      [statename]: value
    }));
  };

  const handleCreateScheduling = (event: FormEvent) => {
    event.preventDefault();

    const isFieldInvalid = validatedFieldInvalid();

    if (isFieldInvalid)
      return false;

    const dateSent = dateTimeGrid || concatDateAndTimeFormatToISODate(dataInput?.date, dataInput?.time);

    const sendObj = {
      schedulingDate: dateSent,
      description: dataInput?.description || ""
    };

    sendScheduling(sendObj);
  };

  const validatedFieldInvalid = () => {

    if (dateTimeGrid)
      return false;

    const isFieldFormInvalid = validateFormField();

    if (isFieldFormInvalid)
      return true;

    return false;
  };

  const validateFormField = () => {

    if (!dataInput.date || !dataInput.time) {
      addErrorField("Favor revisar o preenchimento do formulário");
      return true;
    }

    const isDateInvalid: boolean = validateInvalidDate();
    const isDateInconsistent: boolean = validateRetroactiveDate();

    if (isDateInvalid || isDateInconsistent) return true;

    return false;
  };

  const validateInvalidDate = () => {
    const dateFullYear = new Date(dataInput.date).getFullYear();

    if (dateFullYear.toString().length > 4)
      return addErrorField("Data Inválida.");

    return false;
  };

  const validateRetroactiveDate = () => {
    const dateFormatted = new Date(dataInput.date);
    const dateNow = new Date();

    if (dateFormatted <= dateNow)
      return addErrorField("Informe uma data maior que a data atual");

    return false;
  };

  const sendScheduling = async (sendObj: ScheduleTicketProps) => {
    setIsLoading(true);

    await _ticketService.registerScheduleTicket(sendObj, protocolIdParams || "")
      .then((response) => {
        if (response.status === 200)
          setIsSuccess(true);
      })
      .catch((error: AxiosError<ErrorCatchAxios>) => {
        const isInvalidTicket = error.response?.data.code === "INVALID_TICKET" ?
          EnumErrorCodeRequest.INVALID_TICKET : EnumErrorCodeRequest.GENERIC_ERROR;

        addErrorRequest(isInvalidTicket, error.response?.data.data);
      })
      .finally(() => setIsLoading(false));
  };

  const addErrorRequest = (field: string, msg: string) => {
    setErrorRequest({
      isError: true,
      field: field,
      message: msg || "Ocorreu um erro."
    });

    return true;
  };

  const addErrorField = (msg: string) => {
    setErrorField({
      isError: true,
      message: msg
    });

    return true;
  };

  const handleSelectDateTime = (date: string, time: string) => {
    if (isCheckDateTimeActive(date, time)) {
      setDateTimeGrid("");
      return true;
    }

    const dateTimeConcat: string = concatDateAndTimeFormatToISODate(date, time);
    setDateTimeGrid(dateTimeConcat);

    setDataInput({
      date: "",
      time: "",
      description: dataInput.description
    });
  };

  const isCheckDateTimeActive = (date: string, time: string) => {
    if (dateTimeGrid) {

      const formattedDateString = format(new Date(dateTimeGrid), 'yyyy/MM/dd');
      const timeAddHours = addHours(new Date(dateTimeGrid), 3);
      const formattedTimeString = format(timeAddHours, 'HH:mm');

      if (formattedDateString === date && formattedTimeString === time)
        return true;
    }

    return false;
  };

  const handleBlurClearDateTimeGrid = () => {
    if (dataInput?.date && dataInput?.time && dateTimeGrid)
      setDateTimeGrid("");
  };

  const conditionShowScheduleSuccess = (isSuccess && !errorRequest.isError);
  const conditionShowScheduleError = (!isSuccess && errorRequest.isError);
  const conditionShowSchedule = (!isSuccess && !errorRequest.isError);
  const conditionShowErrorRequest = errorRequest.isError && errorRequest.field === EnumErrorCodeRequest.GENERIC_ERROR;
  const daysGridArray: BusinessDaysArray[] = getValidateAndReturnBusinessDays();

  const descriptionIfCondiction = daysGridArray?.length > 0 ? "grade" : "data/hora";

  return (
    <>
      <Container>
        <div className="header-logo-box mr-2">
          <img
            className="mr-3"
            src={HeaderMagraGroup}
            alt=""
          />
          <img
            src={HeaderLogoCCare}
            alt=""
            width={110}
            height={30}
          />
        </div>

        <Content>

          <Visibility visible={conditionShowScheduleError}>
            <SchedulingWarning
              msgWarning={errorRequest.message}
              errorRequest={errorRequest.field}
            />
          </Visibility>

          <Visibility visible={conditionShowScheduleSuccess}>
            <SchedulingSuccess />
          </Visibility>

          <Visibility visible={conditionShowSchedule}>
            <Text className="color-primary font-bold" size={24}>
              Confirmação do agendamento de entrega
            </Text>

            <Text className="color-primary mt-5" size={16}>
              Nota fiscal  <strong>{nfParams}</strong>
            </Text>

            <Text className="color-primary mb-4" size={16}>
              Utilize a {descriptionIfCondiction} abaixo para confirmar o melhor dia para o agendamento:
            </Text>

            <Visibility visible={(daysGridArray?.length > 0)}>
              <TableGrid>
                {daysGridArray?.map((list: BusinessDaysArray) => (
                  <TableRow key={list.date}>
                    <TableGridDate>
                      <small>{list.nameDays}</small>
                      <p>{list.date}</p>
                    </TableGridDate>

                    {arrayTimes.map((time, index) => (
                      <ButtonGridItem
                        key={index}
                        onClick={() => handleSelectDateTime(list.dateFull, time)}
                        isActive={() => isCheckDateTimeActive(list.dateFull, time)}
                      >
                        {time}
                      </ButtonGridItem>
                    ))}
                  </TableRow>
                ))}
              </TableGrid>

              <small className="pt-2 text-right">
                * Verique com atenção a data escolhida e o calendário de feriados regionais
              </small>
            </Visibility>

            <FormSchedule onSubmit={handleCreateScheduling} id="form-schedule">
              <Visibility visible={(daysGridArray?.length > 0)}>
                <Text className="color-blue-gray pb-1" size={12}>
                  Caso deseje selecionar uma data/hora diferente, utilize os campos abaixo
                </Text>
              </Visibility>

              <Row className="mb-5 flex-sm-column">
                <div className="mr-4 mb-sm-2 relative">
                  <Text className="mb-1 color-blue-gray" size={12}>
                    Data do agendamento
                  </Text>
                  <Input
                    type="date"
                    name="Data"
                    stateName="date"
                    placeholder="Selecione"
                    disabled={isLoading}
                    onBlur={() => handleBlurClearDateTimeGrid()}
                    onChange={handleChange}
                    value={dataInput.date}
                    min={minDate}
                  />
                </div>

                <div className="relative">
                  <Text className=" mb-1 color-blue-gray" size={12}>
                    Horário
                  </Text>
                  <Input
                    type="time"
                    name="Horas"
                    stateName="time"
                    placeholder="Selecione"
                    disabled={isLoading}
                    onBlur={() => handleBlurClearDateTimeGrid()}
                    onChange={handleChange}
                    value={dataInput.time}
                  />
                </div>
              </Row>

              <Text className=" mb-1 color-blue-gray" size={12} weight="bold">
                Deseja adicionar alguma observação?
              </Text>
              <TextArea
                name="Descrição"
                stateName="description"
                placeholder="Mensagem"
                maxLength={255}
                onChange={handleChange}
                disabled={isLoading}
              />

              <Visibility visible={conditionShowErrorRequest}>
                <Text size={12} className="animated fadeIn mt-2 color-danger text-center">
                  {errorRequest.message}
                </Text>
              </Visibility>

              <Visibility visible={errorField.isError}>
                <Text size={12} className="animated fadeIn mt-1 color-danger text-center">
                  {errorField.message}
                </Text>
              </Visibility>

              <Button
                className="outline"
                width={140}
                type="submit"
                form="form-schedule"
                isRequesting={isLoading}
                spinnerColor="#0E4C54"
              >
                <Text className="color-primary font-bold px-1">Agendar</Text>
              </Button>
            </FormSchedule>

          </Visibility>
        </Content>
      </Container >

      <Footer>
        <Column className="px-8 px-lg-4 py-md-4">
          <img src={LogoCompanies} alt="" />
        </Column>
      </Footer>
    </>
  );
};
